import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ElementRef,
  HostListener,
  ChangeDetectorRef,
  OnInit,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ContentChild,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SearchableDropdownOption {
  value: any;
  label: string;
  sublabel?: string;
  icon?: string;
  disabled?: boolean;
  data?: any;
}

@Component({
  selector: 'app-searchable-dropdown',
  templateUrl: './app-searchable-dropdown.component.html',
  styleUrls: ['./app-searchable-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppSearchableDropdownComponent),
      multi: true,
    },
  ],
})
export class AppSearchableDropdownComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() options: any[] = [];
  @Input() valueKey: string = 'id';
  @Input() labelKey: string = 'label';
  @Input() sublabelKey: string = '';
  @Input() placeholder: string = 'Select...';
  @Input() searchPlaceholder: string = 'Search By Customer Name';
  @Input() searchable: boolean = true;
  @Input() fullWidth: boolean = true;
  @Input() minWidth: string = '100%';
  @Input() maxWidth: string = '100%';
  @Input() maxHeight: string = '240px';
  @Input() emptyText: string = 'No customer found';
  @Input() disabled: boolean = false;
  @Input() customClass: string = '';

  // Header / Label
  @Input() label: string = '';
  @Input() labelText: string = '';
  @Input() mandatory: boolean = false;
  @Input() required: boolean = false;

  // Footer Button
  @Input() footerBtnName: string = '';
  @Input() footerBtnIcon: boolean = true;

  // Custom Item Template
  @ContentChild('itemTemplate') itemTemplate?: TemplateRef<any>;

  // Search input element ref for auto-focusing
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  // Outputs
  @Output() selectionChange = new EventEmitter<any>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() footerBtnClick = new EventEmitter<MouseEvent>();
  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  value: any = null;
  isOpen: boolean = false;
  searchTerm: string = '';
  formattedOptions: SearchableDropdownOption[] = [];
  filteredOptions: SearchableDropdownOption[] = [];
  selectedOption: SearchableDropdownOption | null = null;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  get resolvedLabel(): string {
    return this.labelText || this.label || '';
  }

  get isRequired(): boolean {
    return this.mandatory || this.required;
  }

  constructor(private elementRef: ElementRef, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.formatOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.formatOptions();
    }
  }

  formatOptions(): void {
    if (!this.options || !Array.isArray(this.options)) {
      this.formattedOptions = [];
      this.filteredOptions = [];
      this.selectedOption = null;
      return;
    }

    this.formattedOptions = this.options.map((opt) => {
      if (opt && typeof opt === 'object') {
        // Direct option format { value, label }
        if ('value' in opt && 'label' in opt) {
          return {
            value: opt.value,
            label: opt.label,
            sublabel: opt.sublabel,
            icon: opt.icon,
            disabled: !!opt.disabled,
            data: opt.data || opt,
          };
        }

        // Custom object (e.g. Customer entity)
        const val = opt[this.valueKey] !== undefined ? opt[this.valueKey] : opt;
        let lbl = '';
        if (this.labelKey && opt[this.labelKey]) {
          lbl = opt[this.labelKey];
        } else if (opt.prod_sku_name) {
          lbl = opt.prod_sku_name;
        } else if (opt.sku_name) {
          lbl = opt.sku_name;
        } else if (opt.contact_first_name) {
          lbl = `${opt.contact_first_name} ${opt.contact_last_name || ''}`.trim();
        } else if (opt.name) {
          lbl = opt.name;
        } else {
          lbl = String(val);
        }

        const sublbl = this.sublabelKey && opt[this.sublabelKey] ? opt[this.sublabelKey] : undefined;

        return {
          value: val,
          label: lbl,
          sublabel: sublbl,
          icon: opt.icon,
          disabled: !!opt.disabled,
          data: opt,
        };
      }

      // Primitive string/number
      return {
        value: opt,
        label: String(opt),
        disabled: false,
        data: opt,
      };
    });

    this.applyFilter();
    this.updateSelectedOption();
  }

  applyFilter(): void {
    if (!this.searchTerm || !this.searchTerm.trim()) {
      this.filteredOptions = [...this.formattedOptions];
    } else {
      const term = this.searchTerm.toLowerCase().trim();
      this.filteredOptions = this.formattedOptions.filter((opt) =>
        opt.label.toLowerCase().includes(term) ||
        (opt.sublabel && opt.sublabel.toLowerCase().includes(term))
      );
    }
  }

  updateSelectedOption(): void {
    if (this.value === null || this.value === undefined) {
      this.selectedOption = null;
      return;
    }

    this.selectedOption =
      this.formattedOptions.find(
        (opt) => opt.value === this.value || (opt.data && opt.data === this.value)
      ) || null;
  }

  toggleDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.disabled) return;

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (this.disabled || this.isOpen) return;
    this.isOpen = true;
    this.searchTerm = '';
    this.applyFilter();
    this.opened.emit();
    this.cdr.markForCheck();
    this.focusSearchInput();
  }

  focusSearchInput(): void {
    setTimeout(() => {
      if (this.searchInput && this.searchInput.nativeElement) {
        this.searchInput.nativeElement.focus();
      }
    }, 50);
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.searchTerm = '';
    this.applyFilter();
    this.onTouched();
    this.closed.emit();
    this.cdr.markForCheck();
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
    this.applyFilter();
    this.searchChange.emit(this.searchTerm);
  }

  handleClearSearch(event: MouseEvent): void {
    event.stopPropagation();
    this.searchTerm = '';
    this.applyFilter();
    this.searchChange.emit('');
  }

  selectOption(opt: SearchableDropdownOption, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (opt.disabled) return;

    this.value = opt.value;
    this.selectedOption = opt;
    this.onChange(this.value);
    this.selectionChange.emit(opt.data || opt.value);
    this.close();
  }

  onFooterClick(event: MouseEvent): void {
    event.stopPropagation();
    this.footerBtnClick.emit(event);
    this.close();
  }

  // ControlValueAccessor
  writeValue(value: any): void {
    this.value = value;
    this.updateSelectedOption();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  trackByOption(index: number, opt: SearchableDropdownOption): any {
    return opt.value !== undefined ? opt.value : index;
  }
}
